import { useEffect } from 'react';
import { BUSINESS } from '@mopani/contracts';

/**
 * Per-page metadata.
 *
 * Production served ONE generic description on every page, and that description
 * was unedited Replit scaffolding — "Mopani Suppliers — built on Replit. Update
 * this description to reflect the app." (AUD3-012). It was the text in every
 * search result and every WhatsApp link preview, for a WhatsApp-first business.
 *
 * Closes AUD3-012 (C-4) and A-8 (canonical + per-page OG).
 */

type Meta = { title: string; description: string; path: string };

function setTag(selector: string, attr: 'content' | 'href', value: string, create: () => HTMLElement) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

export function usePageMeta({ title, description, path }: Meta) {
  useEffect(() => {
    const full = `${title} | ${BUSINESS.name}`;
    document.title = full;
    const url = `${BUSINESS.publicUrl}${path}`;

    setTag('meta[name="description"]', 'content', description, () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'description');
      return m;
    });
    setTag('link[rel="canonical"]', 'href', url, () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    });

    for (const [prop, value] of [
      ['og:title', full],
      ['og:description', description],
      ['og:url', url],
      ['og:type', 'website'],
      ['og:site_name', BUSINESS.name],
    ] as const) {
      setTag(`meta[property="${prop}"]`, 'content', value, () => {
        const m = document.createElement('meta');
        m.setAttribute('property', prop);
        return m;
      });
    }

    for (const [name, value] of [
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', full],
      ['twitter:description', description],
    ] as const) {
      setTag(`meta[name="${name}"]`, 'content', value, () => {
        const m = document.createElement('meta');
        m.setAttribute('name', name);
        return m;
      });
    }
  }, [title, description, path]);
}

/**
 * LocalBusiness structured data. Closes AUD3-016 (A-7).
 * Production had NO structured data at all — no schema.org, no LocalBusiness,
 * nothing. For a locally-focused B2B supplier that is a real loss of visibility.
 * The telephone here is the authoritative number, from the single BUSINESS source.
 */
export function localBusinessJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: BUSINESS.name,
    description:
      'B2B supplier of professional-grade cleaning and hygiene products to schools, clinics and government offices in the Mopani District, Limpopo.',
    url: BUSINESS.publicUrl,
    telephone: `+27${BUSINESS.phone.slice(1)}`,
    email: BUSINESS.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: BUSINESS.town,
      addressRegion: BUSINESS.province,
      postalCode: BUSINESS.postalCode,
      addressCountry: 'ZA',
    },
    areaServed: BUSINESS.serviceAreas.map((name) => ({ '@type': 'Place', name })),
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '17:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '09:00',
        closes: '13:00',
      },
    ],
  };
}
